/* Permission to view all bookings */
const showBooking = () => 'show:booking';

/* Permission to see technical data (e.g. raw JSON); Only used for rendering purposes, not enforced in backend */
const showTechnicalInfo = () => 'show:technical-info';

/* Permission to open all doors of depot <depotId> */
const openDepotDoors = (depotId: string) => `open:depot:${depotId}`;

/* Permission to open door <doorId> of depot <depotId> */
const openDepotDoor = (depotId: string, doorNumber: number) =>
    `open:depot:${depotId}:door:${doorNumber}`;

/* Permission to edit all locations */
const editDepots = () => 'edit:depot';

/* Permission to read depots */
const readDepots = () => 'depots:read';

/* Permission to write depots */
const writeDepots = () => 'depots:write';

/* Permission to edit location <locationId> */
const editDepot = (depotId: string) => `edit:depot:${depotId}`;

// TODO implement the show permissions for locations
/* Permission to show all locations */
const showLocations = () => 'show:location';

/* Permission to show location <locationId> */
const showLocation = (locationId: string) => `show:location:${locationId}`;

/* Permission to edit all locations */
const editLocations = () => 'edit:location';

/* Permission to edit location <locationId> */
const editLocation = (locationId: string) => `edit:location:${locationId}`;

/* Permission to access booking stats for all depots */
const accessDepotsBookingStats = () => 'statistics:depot';

/* Permission to access booking stats for depot <depotId> */
const accessDepotBookingStats = (depotId: string) =>
    `statistics:depot:${depotId}`;

/* Permission to access booking stats for all locations */
const accessLocationsBookingStats = () => 'statistics:location';

/* Permission to access booking stats for location <locationId> */
const accessLocationBookingStats = (locationId: string) =>
    `statistics:location:${locationId}`;

/* Permission to edit issue report */
const editDamageReports = () => 'edit:damage-report';

/* Permission to edit issue report */
const editIssueReport = (issueRportId: string) =>
    `edit:damage-report:${issueRportId}`;

/* Permission to show bookings sidebar item */
const showBookings = () =>
    'read:bookings';

/* Permission to show lockers sidebar item */
const showLockers = () =>
    'read:lockers';

/* Permission to show bookings sidebar item */
const showIssueReports = () =>
    'read:issue-reports';

/* Permission to show bookings sidebar item */
const showDashboard = () =>
    'read:dashboard';

/* Permission to create a booking */
const createBookings = () =>
    'create:bookings';

/* Permission to read a booking */
const editBookings = () =>
    'edit:bookings';

/* Permission to delete a booking */
const deleteBookings = () =>
    'delete:bookings';

/* Permission to archive / restore a booking (tenant + master) */
const archiveBookings = () =>
    'archive:bookings';

/* Permission to anonymize a booking (master only, irreversible) */
const anonymizeBookings = () =>
    'anonymize:bookings';

/* Permission to create an asset */
const createAssets = () =>
    'create:assets';

/* Permission to delete an asset */
const deleteAssets = () =>
    'delete:assets';

/* Permission to edit a booking */
const readBookings = () =>
    'read:bookings';

/* Permission to create an issue report */
const createIssueReports = () =>
    'create:issue-reports';

/* Permission to read a booking */
const editIssueReports = () =>
    'edit:issue-reports';

/* Permission to archive / restore an issue report (tenant + master) */
const archiveIssueReports = () =>
    'archive:issue-reports';

/* Permission to export the issue-report list as CSV (Owner + Manager, not Service) */
const exportIssueReports = () =>
    'export:issue-reports';

/* Permission to see the Assets overview entry (Owner + Manager) */
const readAssets = () =>
    'read:assets';

/* Permission to create / edit / retire products, categories and location price sheets */
const manageProducts = () =>
    'manage:products';

/* Permission to delete a product, archive / restore / delete a category */
const deleteProducts = () =>
    'delete:products';

/* Permission to list and download invoices */
const readInvoices = () =>
    'read:invoices';

/* Permission to issue, correct and send invoices */
const manageInvoices = () =>
    'manage:invoices';


/* Permission to show visitor booking feature */
const editVisitorBooking = (selectedTenant: string) => `${selectedTenant}:edit:visitor-booking`;

/* Permission to read vouchers — gates the Vouchers & codes sidebar item + page */
const readVouchers = () => 'read:vouchers';

/* Permission to view a voucher's history — its statement (redemptions, refunds,
   chargebacks) and who issued it and why. Its own string so it can be granted
   apart from the list (ADR-0002 amendment 2026-10-13, decision 5; #3261). */
const viewVoucherHistory = () => 'view:voucher-history';

/* Permission to create, edit and cancel promo / goodwill codes and campaigns.
   Its own string rather than the page's read string (PO, 2026-10-07); legacy
   tokens get it from `read:vouchers` (legacyTokenAliases.ts). */
const manageVouchers = () => 'manage:vouchers';

/* Permission to create, edit and delete home-carousel banners. Same bridge as
   manageVouchers — the editor lives on the Vouchers & codes page. */
const manageBanners = () => 'manage:banners';

/* Permission to manage deposit holds — release / capture on the Deposits page */
const manageDeposits = () => 'manage:deposits';

/* Permission to set the per-group deposit amount in the Services area. Owner and
   manager, not service (PO, 2026-10-29). Its own string, apart from releasing or
   capturing holds; legacy tokens get it from `create:assets` (legacyTokenAliases.ts). */
const editDepositSettings = () => 'edit:deposit-settings';

/* Permission to read the Customers surface (organizations, classes, individual
   customers) — mirrors `customers:read` in new-backend/admin rolePermissions.ts
   (H-3b, #1721). The surface is demo-tenant-gated while in preview; adopt these
   for gating when it opens up. */
const readCustomers = () => 'customers:read';

/* Permission to write on the Customers surface — org CRUD + member on-/offboarding.
   Mirrors `customers:write` in new-backend/admin rolePermissions.ts (H-3b, #1721). */
const writeCustomers = () => 'customers:write';

const Permission = {
    showBooking,
    showTechnicalInfo,
    openDepotDoors,
    openDepotDoor,
    showLocations,
    showLocation,
    editLocations,
    editLocation,
    editDepots,
    editDepot,
    readDepots,
    writeDepots,
    accessDepotsBookingStats,
    accessDepotBookingStats,
    accessLocationsBookingStats,
    accessLocationBookingStats,
    editDamageReports,
    editIssueReport,
    editVisitorBooking,
    readVouchers,
    viewVoucherHistory,
    manageVouchers,
    manageBanners,
    manageDeposits,
    editDepositSettings,
    readCustomers,
    writeCustomers,
    showBookings,
    showLockers,
    showIssueReports,
    showDashboard,
    createBookings,
    editBookings,
    deleteBookings,
    archiveBookings,
    anonymizeBookings,
    createAssets,
    deleteAssets,
    readBookings,
    createIssueReports,
    editIssueReports,
    archiveIssueReports,
    exportIssueReports,
    readAssets,
    manageProducts,
    deleteProducts,
    readInvoices,
    manageInvoices,
};

export default Permission;
